import { createConsumer } from '@rails/actioncable';
import {
  clearSafeTimeout,
  scheduleSafeTimeout,
} from '../../dashboard/helper/browserTimers';

const PRESENCE_INTERVAL = 20000;
const PRESENCE_JITTER = 5000;
const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 8000;

class BaseActionCableConnector {
  static hasPendingReconnect = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.app = app;
    this.events = {};
    this.presenceTimer = null;
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.reconnectPending = false;
    this.isIntentionallyDisconnected = false;
    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence', {
            visible:
              typeof document !== 'undefined' &&
              document.visibilityState === 'visible',
          });
        },
        received: this.onReceived,
        connected: this.handleConnected,
        disconnected: this.handleDisconnected,
      }
    );
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      const jitter = Math.floor(Math.random() * PRESENCE_JITTER);
      this.clearPresenceTimer();
      this.presenceTimer = scheduleSafeTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL + jitter);
    };
    this.triggerPresenceInterval();
  }

  handleConnected = () => {
    if (this.isIntentionallyDisconnected) return;

    const shouldNotifyReconnect =
      this.reconnectPending || BaseActionCableConnector.hasPendingReconnect;
    this.reconnectPending = false;

    if (!shouldNotifyReconnect) return;

    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    BaseActionCableConnector.hasPendingReconnect = false;
    this.onReconnect();
  };

  handleDisconnected = () => {
    if (this.isIntentionallyDisconnected) return;
    if (this.reconnectPending) return;

    const shouldNotifyDisconnect =
      !BaseActionCableConnector.hasPendingReconnect;
    this.reconnectPending = true;
    BaseActionCableConnector.hasPendingReconnect = true;

    if (shouldNotifyDisconnect) {
      this.onDisconnected();
    }
    this.initReconnectTimer();
  };

  checkConnection() {
    if (this.isIntentionallyDisconnected) return;
    const isConnectionActive = this.consumer.connection.isOpen();
    if (isConnectionActive) {
      this.handleConnected();
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearSafeTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  clearPresenceTimer = () => {
    if (this.presenceTimer) {
      clearSafeTimeout(this.presenceTimer);
      this.presenceTimer = null;
    }
  };

  initReconnectTimer = () => {
    if (this.isIntentionallyDisconnected) return;
    this.clearReconnectTimer();
    const backoff = Math.min(
      RECONNECT_BASE * 2 ** this.reconnectAttempts,
      RECONNECT_MAX
    );
    const jitter = Math.floor(Math.random() * backoff);
    this.reconnectAttempts += 1;
    this.reconnectTimer = scheduleSafeTimeout(() => {
      this.checkConnection();
    }, backoff + jitter);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect({ preservePendingReconnect = false } = {}) {
    this.isIntentionallyDisconnected = true;
    this.reconnectPending = false;
    if (!preservePendingReconnect) {
      BaseActionCableConnector.hasPendingReconnect = false;
    }
    this.clearPresenceTimer();
    this.clearReconnectTimer();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data, event)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
